import React, { useState, useRef } from "react";
import Plot from "react-plotly.js";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { FileExcel } from 'react-bootstrap-icons';
import { Download } from "@mui/icons-material";
import ExcelJS from "exceljs";
import html2canvas from "html2canvas";

const darken = (hex, amt = 55) => {
  try {
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
    const n = parseInt(c, 16);
    const clamp = (v) => Math.min(255, Math.max(0, v));
    const r = clamp(((n >> 16) & 0xff) - amt);
    const g = clamp(((n >> 8) & 0xff) - amt);
    const b = clamp((n & 0xff) - amt);
    const toHex = (v) => v.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  } catch {
    return '#444';
  }
};

const BarChartCompostCustom = ({ data, namekey, datakey, datakey2, title, nameeje, nameeje2, nameejevertical, nameejevertical2, observaciones }) => {
  const [hiddenItems, setHiddenItems] = useState([]);
  const chartRef = useRef(null);

  // Función para dividir texto largo en dos líneas
  const formatLabel = (text) => {
    if (!text || text.length <= 14) return text;
    const mid = Math.floor(text.length / 2);
    const spaceIndex = text.indexOf(' ', mid - 7);
    if (spaceIndex > 0 && spaceIndex < mid + 7) {
      return text.substring(0, spaceIndex) + '<br>' + text.substring(spaceIndex + 1);
    }
    return text.substring(0, mid) + '<br>' + text.substring(mid);
  };

  const chartData = data.map((item) => ({
    name: item[namekey],
    value1: item[datakey],
    value2: item[datakey2],
  }));

  const filteredData = chartData.filter((item) => !hiddenItems.includes(item.name));

  const xValues = filteredData.map((item) => formatLabel(item.name));
  const y1Values = filteredData.map((item) => item.value1);
  const y2Values = filteredData.map((item) => item.value2);

  const plotData = [
    {
      type: 'bar',
      name: nameejevertical || 'Serie 1',
      x: xValues,
      y: y1Values,
      marker: {
        color: '#8884d8',
        line: {
          color: darken('#8884d8', 60),
          width: 2.2
        }
      },
      hovertemplate: '<b>%{x}</b><br>' +
                     nameejevertical + ': %{y:,.0f}<br>' +
                     '<extra></extra>',
    },
    {
      type: 'bar',
      name: nameejevertical2 || 'Serie 2',
      x: xValues,
      y: y2Values,
      marker: {
        color: '#82ca9d',
        line: {
          color: darken('#82ca9d', 60),
          width: 2.2
        }
      },
      hovertemplate: '<b>%{x}</b><br>' +
                     nameejevertical2 + ': %{y:,.0f}<br>' +
                     '<extra></extra>',
    }
  ];

  const layout = {
    title: {
      text: '',
      font: { size: 18, color: '#344767' }
    },
    barmode: 'group',
    xaxis: {
      title: nameeje || 'Categorías',
      tickangle: -45,
      tickfont: {
        size: 10
      }
    },
    yaxis: {
      title: 'Valores'
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    showlegend: true,
    legend: {
      orientation: 'h',
      x: 0.5,
      xanchor: 'center',
      y: -0.25,
      yanchor: 'top'
    },
    margin: { l: 60, r: 50, t: 40, b: 100 },
    height: 450,
  };

  const config = {
    displayModeBar: false,
    displaylogo: false,
  };

  const handleLegendClick = (itemName) => {
    setHiddenItems((prev) => {
      if (prev.includes(itemName)) {
        return prev.filter((name) => name !== itemName);
      } else {
        return [...prev, itemName];
      }
    });
  };

  const handleSelectAll = () => {
    setHiddenItems([]);
  };

  const handleSelectNone = () => {
    setHiddenItems(chartData.map(item => item.name));
  };

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Datos");

    const headers = [nameeje || 'Categoría', nameejevertical || 'Serie 1', nameejevertical2 || 'Serie 2'];
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "1976d2" }
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true };
    });

    chartData.forEach((item) => {
      worksheet.addRow([item.name, item.value1, item.value2]);
    });

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title || 'grafico'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    });
  };

  const handleDownloadPNG = async () => {
    if (chartRef.current) {
      try {
        const canvas = await html2canvas(chartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const link = document.createElement('a');
        link.download = `${title || 'grafico'}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch (error) {
        console.error('Error al descargar el gráfico:', error);
      }
    }
  };

  const renderLegend = () => {
    return (
      <MDBox>
        <MDBox style={{ display: 'flex', gap: '8px', justifyContent: 'center', paddingTop: '10px', paddingBottom: '8px' }}>
          <MDBox
            onClick={handleSelectAll}
            style={{
              cursor: 'pointer',
              padding: '4px 12px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              fontSize: '0.7rem',
              color: '#1976d2',
              fontWeight: '500',
            }}
          >
            Seleccionar todos
          </MDBox>
          <MDBox
            onClick={handleSelectNone}
            style={{
              cursor: 'pointer',
              padding: '4px 12px',
              backgroundColor: '#fce4ec',
              borderRadius: '4px',
              fontSize: '0.7rem',
              color: '#c2185b',
              fontWeight: '500',
            }}
          >
            Seleccionar ninguno
          </MDBox>
        </MDBox>
        <MDBox style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', paddingTop: '10px' }}>
        {chartData.map((entry, index) => (
          <MDBox
            key={`legend-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              opacity: hiddenItems.includes(entry.name) ? 0.4 : 1,
              transition: 'opacity 0.3s',
            }}
            onClick={() => handleLegendClick(entry.name)}
          >
            <MDBox
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: '#8884d8',
                marginRight: '6px',
                borderRadius: '2px',
              }}
            />
            <MDTypography variant="caption" fontSize="0.7rem" color="text">
              {entry.name}
            </MDTypography>
          </MDBox>
        ))}
        </MDBox>
      </MDBox>
    );
  };

  if (!data || data.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          No hay datos disponibles
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={3}>
      <MDBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <MDTypography variant="h5" fontWeight="medium">
          {title}
        </MDTypography>
        <MDBox style={{ display: 'flex', gap: '8px' }}>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<FileExcel />}
            onClick={exportToExcel}
          >
            Excel
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Download />}
            onClick={handleDownloadPNG}
          >
            PNG
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox 
        sx={{ 
          backgroundColor: 'white', 
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 14px 28px rgba(0,0,0,0.22)',
          filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.18))'
        }}
        ref={chartRef}
      >
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </MDBox>

      {renderLegend()}

      {observaciones && (
        <MDTypography variant="caption" color="text" mt={2} style={{ fontStyle: 'italic', opacity: 0.7 }}>
          *{observaciones}
        </MDTypography>
      )}
    </MDBox>
  );
};

export default BarChartCompostCustom;
